/**
 * 群組節點組件
 * 遞歸渲染群組節點，處理展開/收合、選擇
 */

import * as React from "react";
import { Tree, TreeItem, TreeItemLayout, Checkbox, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { ChevronRight20Regular, ChevronDown20Regular, PeopleCommunity20Color } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import MemberNode from "./MemberNode";

const useStyles = makeStyles({
  groupItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 8px 8px 0",
    margin: "2px 0",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    fontWeight: "500",
    fontSize: "14px",
  },
  groupItemSelected: {
    backgroundColor: tokens.colorNeutralBackground4,
    borderLeft: `3px solid #0078D4`,
    transform: "translateX(0)",
    paddingLeft: "3px",
  },
  groupIcon: {
    color: "#0078D4",
    height: "16px !important",
  },
  loadingContainer: {
    padding: "8px 20px 8px 0",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
  },
  treeItemLayout: {
    paddingLeft: "0 !important",
    marginLeft: "0 !important",
  },
});

const GroupTreeNode = ({
  group,
  isSelected,
  isExpanded,
  isParentExpanded,
  level,
  nodePath,
  loadedSubgroups,
  members,
  isLoading,
  selectedMembers,
  onToggleExpand,
  onToggleSelect,
  onToggleMember,
  renderChildNode,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const hasContent = loadedSubgroups.length > 0 || members.length > 0 || isLoading;
  const shouldShowExpandedColor = isExpanded || isParentExpanded;

  const handleClick = (e) => {
    // 如果點擊的是 checkbox 區域，阻止事件冒泡
    if (e.target.closest && (
      e.target.closest('input[type="checkbox"]') || 
      e.target.closest('[role="checkbox"]') ||
      e.target.closest('label')
    )) {
      e.stopPropagation();
    }
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    onToggleExpand(nodePath, !isExpanded);
  };

  return (
    <TreeItem
      key={nodePath}
      value={nodePath}
      itemType="branch"
      open={isExpanded}
      onOpenChange={(event, data) => {
        // 如果點擊的是 checkbox，不執行展開邏輯
        if (event?.target?.closest && (
          event.target.closest('input[type="checkbox"]') || 
          event.target.closest('[role="checkbox"]') ||
          event.target.closest('label')
        )) {
          return;
        }
        const path = data?.value || data?.item?.value || nodePath;
        if (path) {
          const shouldExpand = data?.open !== undefined ? data.open : !isExpanded;
          onToggleExpand(path, shouldExpand);
        }
      }}
    >
      <TreeItemLayout
        className={styles.treeItemLayout}
        data-expanded={shouldShowExpandedColor ? "true" : "false"}
        data-level={level}
        onClick={handleClick}
        iconBefore={
          <Checkbox
            checked={isSelected}
            onChange={(e, data) => {
              e.stopPropagation();
              onToggleSelect(group.id, data.checked);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            size="medium"
          />
        }
        expandIcon={null}
        aside={
          hasContent ? (
            <div 
              onClick={handleExpandClick}
              style={{ 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '4px'
              }}
            >
              {isExpanded ? (
                <ChevronDown20Regular />
              ) : (
                <ChevronRight20Regular />
              )}
            </div>
          ) : <ChevronRight20Regular />
        }
      >
        <div className={`${styles.groupItem} ${isSelected ? styles.groupItemSelected : ''}`}>
          <PeopleCommunity20Color className={styles.groupIcon} />
          <span>{group.displayName.split('.')[0]}</span>
        </div>
      </TreeItemLayout>
      
      {isExpanded && (
        <Tree>
          {/* 顯示載入中 */}
          {isLoading && (
            <div className={styles.loadingContainer}>
              <Spinner size="tiny" />
              <span>{t('loading_members')}</span>
            </div>
          )}
          
          {/* 顯示成員 */}
          {members.length > 0 && members.map(member => (
            <MemberNode
              key={member.id}
              member={member}
              isSelected={selectedMembers?.has(member.id) || false}
              isParentExpanded={isExpanded}
              level={level + 1}
              onToggle={onToggleMember}
            />
          ))}

          {/* 顯示子群組（遞歸渲染） */}
          {loadedSubgroups.length > 0 && loadedSubgroups.map((child) => 
            renderChildNode(child, isExpanded, level + 1, nodePath)
          )}
        </Tree>
      )}
    </TreeItem>
  );
};

export default GroupTreeNode;

